/**
 * Arrange mode's browser rules, kept once for both arrange pages: a report's edit mode
 * (report-page.js) and the Site Analytics page's (site-analytics-edit.js). Each page keeps its own
 * words and its own focus targets; how a focus instruction reaches a control inside a closed
 * disclosure, and how the Add chart panel searches, counts, shrinks for a drag and moves focus on
 * after an add, live here.
 */

import { focusInView } from './focus-in-view';

/**
 * Raised on a control a focus instruction names when it sits in a closed disclosure panel (a card's
 * Move menu, a toolbar folded behind "⋯", a report tab's options): the disclosure around it opens.
 * A morph can re-initialise a card it moves, which closes its menus.
 */
export const DISCLOSURE_OPEN_EVENT = 'arrange-disclosure-open';

/** A disclosure panel a focus instruction may have to open first (components/arrange/edit-toolbar.blade.php). */
const DISCLOSURE_PANEL = '[data-arrange-disclosure-panel]';

/**
 * Focus an element a focus instruction named, opening the disclosure it sits in first when that is
 * closed, so focus is never sent to a control nobody can see, and scrolling it clear of the pinned
 * edit bar and filter header (focusInView()): a card moved earlier lands where the card before it
 * began, which can be under them. `select` selects a field's text too.
 */
export function focusRevealed(target, select = false) {
    if (! target) return;

    const land = () => {
        focusInView(target);

        if (select && typeof target.select === 'function') target.select();
    };

    if (target.closest?.(DISCLOSURE_PANEL) && target.offsetParent === null) {
        target.dispatchEvent(new CustomEvent(DISCLOSURE_OPEN_EVENT, { bubbles: true }));

        // Convention exception — a bounded wait for the element to show:
        // when the morph moves a keyed card (Move later), Livewire copies the server's markup
        // onto the moved node and Alpine re-applies its state to it a few frames later, so a
        // menu that is open in Alpine's state is hidden (its x-cloak put back) for that long.
        // The real fix is upstream in Livewire's morph of moved keyed nodes; this waits at
        // most about half a second, then gives up rather than focusing a hidden control.
        const reveal = (frames) => {
            for (let panel = target.closest(DISCLOSURE_PANEL); panel; panel = panel.parentElement?.closest(DISCLOSURE_PANEL) ?? null) {
                panel.removeAttribute('x-cloak');
            }

            if (target.offsetParent === null && frames > 0) {
                requestAnimationFrame(() => reveal(frames - 1));

                return;
            }

            if (target.offsetParent !== null) land();
        };

        requestAnimationFrame(() => reveal(30));

        return;
    }

    land();
}

/** How long after the last search change the panel's count is said. */
export const PALETTE_ANNOUNCE_DELAY = 400;

/**
 * Text as the palette's search compares it: lower case, accents dropped, so "cafe" finds "Café".
 * The same folding on both sides, so what matches does not depend on how a name was typed.
 */
export function foldForSearch(text) {
    return String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** The words of a search, folded; none for a blank one. */
export function searchWords(query) {
    return foldForSearch(query).split(/\s+/).filter((word) => word !== '');
}

/**
 * Whether a palette entry's text matches a search: every word, anywhere in it, as the app's
 * other searches match (TokenSearchUtils), so "revenue stripe" finds Stripe's Revenue. A blank
 * search matches everything.
 */
export function paletteMatches(haystack, query) {
    const words = searchWords(query);

    if (words.length === 0) return true;

    const folded = foldForSearch(haystack);

    return words.every((word) => folded.includes(word));
}

/** The count sentence, or the empty one when nothing is shown out of a list that has entries. */
export function paletteCountSentence(text, shown, total) {
    if (shown === 0 && total > 0) return text.paletteNone ?? 'No metrics match your search.';

    return String(text.paletteCount ?? 'Metrics shown: :shown of :total.')
        .split(':shown').join(String(shown))
        .split(':total').join(String(total));
}

/** The short count beside a section heading of the Add chart panel: ":shown of :total". */
export function paletteShownText(text, shown, total) {
    return String(text.paletteShown ?? ':shown of :total')
        .split(':shown').join(String(shown))
        .split(':total').join(String(total));
}

/** How long a card added from the panel keeps its "Just added" outline and label, in ms. */
export const PALETTE_JUST_ADDED_MS = 4000;

/** The attribute a card just added from the panel wears, which resources/css/misc.css outlines and labels. */
export const JUST_ADDED_ATTRIBUTE = 'data-arrange-just-added';

/**
 * The Add button focus goes to after an add from the panel: the entry's own, else, when that entry
 * left the list or its Add can take no focus (Add row mode marks a metric just added as a row
 * and disables it), the next entry's that can, then the previous one's. Null when none can.
 * `entries` are the panel's entries in order, each with data-palette-key.
 */
export function paletteAddAfter(entries, key) {
    const usable = (entry) => {
        const button = entry?.querySelector?.('[data-palette-add]');

        return button && ! button.disabled && button.offsetParent !== null ? button : null;
    };
    const at = entries.findIndex((entry) => entry?.getAttribute?.('data-palette-key') === key);

    if (at === -1) return null;

    for (let index = at; index < entries.length; index++) {
        const button = usable(entries[index]);

        if (button) return button;
    }

    for (let index = at - 1; index >= 0; index--) {
        const button = usable(entries[index]);

        if (button) return button;
    }

    return null;
}

/**
 * A card just added from the panel: outlined and labelled "Just added" for a few seconds, and,
 * while the panel is the fixed side panel, scrolled into view without moving focus out of the panel
 * (at once under prefers-reduced-motion). Below lg the panel sits above the grid, and scrolling
 * would carry the focused Add off screen. `card` is the card's element; `panel` the open panel's.
 */
export function showJustAdded(card, panel, label) {
    if (! card) return;

    card.setAttribute(JUST_ADDED_ATTRIBUTE, label ?? 'Just added');
    clearTimeout(card._arrangeJustAdded);
    card._arrangeJustAdded = setTimeout(() => card.removeAttribute(JUST_ADDED_ATTRIBUTE), PALETTE_JUST_ADDED_MS);

    if (! panel || typeof getComputedStyle !== 'function' || getComputedStyle(panel).position !== 'fixed') return;

    const isReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

    card.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: isReduced ? 'auto' : 'smooth' });
}

/**
 * Keeps a fixed Add chart panel (lg up) flush with the top of what the page shows: right under the
 * app's top bar while that is in view, then on the scroll area's top edge, where edit mode's bar pins
 * (sticky, top-0), so no band of page shows above the panel at any scroll position. Measured off the
 * layout rather than assumed, and written as `variable` on the document element, as the edit bar's
 * height is, since each render puts the panel's own attributes back. Its listeners end themselves
 * once the panel element is gone. `offsetVariable` names a measured height the page pins above its
 * content too (the Insights top bar), which the panel stays below.
 */
export function pinPanelTop(el, variable, offsetVariable = null) {
    const scroller = el?.closest?.('[data-ref="app-main-scrollable-container"]');
    const content = el?.closest?.('[data-ref="app-main-content-container"]');

    if (! scroller || ! content) return;

    const place = () => {
        if (! el.isConnected) {
            scroller.removeEventListener('scroll', place);
            window.removeEventListener('resize', place);

            return;
        }

        // Below a bar the page pins at the top of its content (the Insights top bar), when it has one.
        const offset = offsetVariable ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue(offsetVariable)) || 0 : 0;
        const top = Math.max(scroller.getBoundingClientRect().top, content.getBoundingClientRect().top) + offset;

        document.documentElement.style.setProperty(variable, `${Math.round(top)}px`);
    };

    place();
    scroller.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place, { passive: true });
}

/**
 * The Add chart panel's browser half for a page whose panel is a search over entries in sections
 * (Site Analytics'), for the page's Alpine scope to spread in. Its entries carry
 * `data-palette-key` and `data-palette-search` and sit in sections marked
 * `data-palette-section="<name>"`; its search field is `options.search`.
 *
 *  - Filtering is x-show over the server's markup: typing sends nothing to the server, a morph
 *    keeps what is shown, and a hidden entry leaves the tab order and the accessibility tree.
 *  - The count is said through the page's arrange region (options.announce, called with the scope
 *    as `this`) once the reader pauses, never per keystroke; the counts beside the section headings
 *    are its visible copies.
 *  - Escape in a search holding text clears it and stops; otherwise it closes the panel, whose
 *    answer from the server returns focus to what opened it.
 *  - Pressing an entry to drag it shrinks the panel to that entry, hidden in place (no layout
 *    shift below lg, where the panel sits in the page's flow), since the browser only drags an
 *    element it draws; once the entry is dragged away the panel is hidden, so the grid comes up
 *    under the pointer; the drag's end closes it on the server; a press let go without a drag shows
 *    it whole again, and so does a drag let go over the page's pinned headers (pal_dropCancelled()).
 *
 * @param {{ search: string, panel: string, sections: Record<string, boolean>, text: Record<string, string>, announce: (message: string) => void }} options
 */
export function arrangePalette(options) {
    let announceTimer = null;
    let queryFor = null;
    let words = [];
    const folded = new Map();
    let dropCancelled = false;

    return {
        /** What the panel's search holds. */
        pal_query: '',

        /** An entry is pressed to drag: the panel shows that entry alone. */
        pal_holding: false,

        /** The pressed entry has been dragged away: the panel is hidden until the server closes it. */
        pal_gone: false,

        /** Section name => whether it is open. */
        pal_sections: { ...options.sections },

        /**
         * The panel opened (its root's x-init): everything shown, each section as it starts. Kept
         * through the re-renders while it stays open, since x-init does not run again on a morphed
         * element.
         */
        pal_reset() {
            this.pal_query = '';
            this.pal_holding = false;
            this.pal_gone = false;
            this.pal_sections = { ...options.sections };
            clearTimeout(announceTimer);
        },

        /** Something was grabbed to drag (`arrange-grab`): one of the panel's own entries shrinks it to that entry. */
        pal_grabbed(panel, item) {
            dropCancelled = false;

            if (panel?.contains?.(item)) this.pal_holding = true;
        },

        /** The held entry has left the panel (SortableJS's `start` on its list). */
        pal_dragged() {
            this.pal_gone = true;
        },

        /** The press on an entry was let go without a drag (SortableJS's `unchoose`). */
        pal_released() {
            if (! this.pal_gone) this.pal_holding = false;
        },

        /** The drag was let go over the page's pinned headers and put back into the panel (`arrange-drop-cancel`). */
        pal_dropCancelled() {
            dropCancelled = true;
        },

        /** The drag is over (SortableJS's `end`): the server closes the panel, unless the drag was put back. */
        pal_dropped() {
            if (dropCancelled) {
                dropCancelled = false;
                this.pal_holding = false;
                this.pal_gone = false;

                return;
            }

            this.$wire.closePalette();
        },

        /** Whether an entry matches the search. */
        pal_textShows(el) {
            const query = this.pal_query;

            if (query !== queryFor) {
                queryFor = query;
                words = searchWords(query);
            }

            if (words.length === 0) return true;

            const raw = el?.getAttribute?.('data-palette-search') ?? '';
            let text = folded.get(raw);

            if (text === undefined) {
                text = foldForSearch(raw);
                folded.set(raw, text);
            }

            return words.every((word) => text.includes(word));
        },

        /** How many entries the search leaves shown, of how many are listed: in one section, or the whole panel. */
        pal_counts(section = null) {
            const scope = document.querySelector(options.panel);
            const selector = section === null
                ? '[data-palette-search]'
                : `[data-palette-section="${section}"] [data-palette-search]`;
            const entries = [...(scope?.querySelectorAll?.(selector) ?? [])];

            return { shown: entries.filter((entry) => this.pal_textShows(entry)).length, total: entries.length };
        },

        /** The count beside a section's heading. */
        pal_shownText(section) {
            const { shown, total } = this.pal_counts(section);

            return paletteShownText(options.text, shown, total);
        },

        /** Whether a section has an entry the search leaves shown. */
        pal_sectionShows(section) {
            return this.pal_counts(section).shown > 0;
        },

        /** Whether the search leaves nothing shown of a panel that lists something. */
        pal_isEmpty() {
            const { shown, total } = this.pal_counts();

            return total > 0 && shown === 0;
        },

        /** A section heading's button: its section shown or hidden. */
        pal_toggleSection(name) {
            this.pal_sections[name] = ! this.pal_sections[name];
        },

        /** The search changed: the list narrows at once, and the count is said once the reader pauses. */
        pal_changed() {
            clearTimeout(announceTimer);
            announceTimer = setTimeout(() => {
                const { shown, total } = this.pal_counts();

                options.announce.call(this, paletteCountSentence(options.text, shown, total));
            }, PALETTE_ANNOUNCE_DELAY);
        },

        /** An action's own sentence is about to be said: a count still waiting would talk over it. */
        pal_quiet() {
            clearTimeout(announceTimer);
        },

        /** The empty result's way out: everything shown again, and focus in the search. */
        pal_clear() {
            this.pal_query = '';
            this.pal_changed();
            document.querySelector(options.search)?.focus?.();
        },

        /**
         * Escape in the search clears it when it holds text, and stops there; in an empty search it
         * goes on to the panel, which closes. Otherwise one Escape would both clear the search (the
         * browser's own) and close the panel.
         */
        pal_escape(event) {
            if (this.pal_query === '') return;

            event?.preventDefault?.();
            event?.stopPropagation?.();
            this.pal_query = '';
            this.pal_changed();
        },

        /** Escape anywhere in the panel that nothing inside handled: the panel closes. */
        pal_escapeKey() {
            this.$wire.togglePalette();
        },

        /** An entry's Add: added where the panel adds, its key read off the entry. */
        pal_add(el) {
            this.$wire.addCard(el?.closest?.('[data-palette-key]')?.getAttribute?.('data-palette-key') ?? '');
        },
    };
}
